/**
 * FacturationTab — Embedded facturation for a specific affaire
 * Reuses the admin facturation components (Products, Invoices, Credit Notes)
 * scoped to the affaire, like the tracker facturation flow.
 */

import React, { useState } from 'react';
import { ProductsEmbeddedView } from '@/features/admin/tabs/facturation/components/ProductsEmbeddedView/ProductsEmbeddedView';
import { InvoicesListView } from '@/features/admin/tabs/facturation/components/InvoicesView/InvoicesListView';
import { CreditNotesListView } from '@/features/admin/tabs/facturation/components/CreditNotesView/CreditNotesListView';
import type { InvoiceFilters } from '@/features/admin/tabs/facturation/types/facturation.types';

interface FacturationTabProps {
  affaireId: number;
}

type SubTab = 'produits' | 'factures' | 'notes-credit';

export const FacturationTab: React.FC<FacturationTabProps> = ({ affaireId }) => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('factures');

  const affaireIdStr = String(affaireId);

  const invoiceFilters: InvoiceFilters = {
    search: affaireIdStr,
    page: 1,
    limit: 50,
    sort_by: 'created_at',
    sort_order: 'DESC',
  };

  const searchContext = {
    mode: 'affaire' as const,
    term: affaireIdStr,
  };

  return (
    <div className="aff-detail-facturation">
      {/* Sub-tab toggle */}
      <div className="aff-detail-fact-subtabs">
        {([
          { key: 'produits' as const, label: 'Produits' },
          { key: 'factures' as const, label: 'Factures' },
          { key: 'notes-credit' as const, label: 'Notes de crédit' },
        ]).map(tab => (
          <button
            key={tab.key}
            className={`aff-detail-fact-subtab ${activeSubTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveSubTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="aff-detail-fact-content">
        {activeSubTab === 'produits' && (
          <ProductsEmbeddedView affaireId={affaireIdStr} />
        )}
        {activeSubTab === 'factures' && (
          <InvoicesListView
            initialFilters={invoiceFilters}
            searchLabel={`Affaire ${affaireIdStr}`}
            searchContext={searchContext}
          />
        )}
        {activeSubTab === 'notes-credit' && (
          <CreditNotesListView
            initialFilters={invoiceFilters}
            searchLabel={`Affaire ${affaireIdStr}`}
            searchContext={searchContext}
          />
        )}
      </div>
    </div>
  );
};
